import { Component, Input, OnInit, SecurityContext, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogActions, MatDialogTitle } from '@angular/material/dialog';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { PtsPaymentTerminal, PtsPaymentTerminalSearchEvent } from '../../payment-terminal/pts/pts-payment-terminal-model';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, firstValueFrom, startWith, tap } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { PtsTerminalConnectorRelationDialogComponent } from '../../payment-terminal/pts/dialogs/terminal-connector-relation-dialog/terminal-connector-relation-dialog.component';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AuthService } from 'src/app/core/auth/auth.service';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { FeedbackInfoComponent } from 'ok-angular-shared/components';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'adm-choose-payter-terminal-modal',
  templateUrl: './choose-payter-terminal-modal.component.html',
  styleUrl: './choose-payter-terminal-modal.component.scss',
  imports: [
    LoadingSpinnerComponent,
    CustomDropDownArrowDirective,
    FeedbackInfoComponent,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatFormField,
    MatLabel,
    MatButton,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatInput,
    MatInputModule,
    MatIcon,
    MatButton,
    ReactiveFormsModule,
    CommonModule,
  ],
})
export class ChoosePayterTerminalModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ChoosePayterTerminalModalComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private paymentTerminalService = inject(PtsPaymentTerminalService);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  data = inject<{
    chargingStationIdentifier: string;
    organizationId: number;
  }>(MAT_DIALOG_DATA);

  public typeAheadTerminalModel: FormGroup;
  public chargingStationIdentifier: string;
  public organizationId: number;
  public roles = this.environmentLoaderService.virtualRoles;
  filteredSerialNumbers: string[];
  selectedTerminal: PtsPaymentTerminal | null = null;
  @Input() loading$: BehaviorSubject<boolean>;

  async ngOnInit(): Promise<void> {
    this.loading$ = new BehaviorSubject(false);
    this.chargingStationIdentifier = this.data.chargingStationIdentifier;
    this.organizationId = this.data.organizationId;
    this.typeAheadTerminalModel = this.initTerminalTypeAhead();
  }

  public initTerminalTypeAhead() {
    this.loading$.next(true);
    const typeAheadFormGroup = new FormGroup({
      typeAheadQuery: new FormControl(''),
    });

    typeAheadFormGroup.valueChanges
      .pipe(
        filter(val => {
          const queryLength = val.typeAheadQuery?.length ?? 0;
          return queryLength >= 2 || queryLength === 0;
        }),
        startWith(''), // let one blank through to search for all
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe((val: any) => {
        const paymentTerminalSearchEvent = new PtsPaymentTerminalSearchEvent();
        paymentTerminalSearchEvent.query = val.typeAheadQuery;
        this.paymentTerminalService.getSerialNumbersWithoutAttachedConnectors().subscribe(x => {
          this.loading$.next(false);
          this.filteredSerialNumbers = x;
        });
      });
    return typeAheadFormGroup;
  }

  public resetTerminalFilter() {
    this.selectedTerminal = null;
    this.typeAheadTerminalModel.controls?.typeAheadQuery?.setValue('');
  }
  public async selectSerialNumber(serialNumber: string) {
    const paymentTerminalSearchEvent: PtsPaymentTerminalSearchEvent = new PtsPaymentTerminalSearchEvent();
    paymentTerminalSearchEvent.query = serialNumber;
    const paymentTerminal = (await firstValueFrom(this.paymentTerminalService.getPaymentTerminals(paymentTerminalSearchEvent)))?.items[0];

    if (paymentTerminal) {
      this.selectedTerminal = paymentTerminal;
      this.openTerminalConnectorRelationModal();
    }
  }
  public displayFn(ptsPaymentTerminal: PtsPaymentTerminal): string {
    return ptsPaymentTerminal && ptsPaymentTerminal.serialNumber ? ptsPaymentTerminal.serialNumber : '';
  }
  public openTerminalConnectorRelationModal() {
    const ptsTerminalConnectorRelationModal = this.dialog.open(PtsTerminalConnectorRelationDialogComponent, {
      data: {
        paymentTerminal: this.selectedTerminal,
        organizationId: this.organizationId,
        chargingStationIdentifier: this.chargingStationIdentifier,
        fromTerminalConnectorRelationModal: true,
      },
      width: '760px',
    });

    ptsTerminalConnectorRelationModal.afterClosed().subscribe((paymentTerminal: PtsPaymentTerminal) => {
      if (paymentTerminal) {
        this.dialogRef.close(paymentTerminal);
      }
    });
  }
}
